import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { collection, query, where, getDocs, orderBy, doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useLocation } from "react-router-dom";
import getReceiptUrl from "../services/receiptService";
import ReceiptButton from "../components/receipt/ReceiptButton";
import { sendReceiptNotification } from "../services/emailService";
import { handleCheckoutSuccess } from "../services/stripeService";
import TotalAmountCard from "../components/common/TotalAmountCard";
import { usePageTitle } from "../context/PageTitleContext";
import "./Dashboard.scss";

function Dashboard() {
  const { user } = useSelector((state) => state.auth);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptProcessed, setReceiptProcessed] = useState(false);
  const receiptLoadingRef = useRef(false);
  const location = useLocation();
  const { setPageTitle } = usePageTitle();

  // Set page title when component mounts
  useEffect(() => {
    setPageTitle("Dashboard");
  }, [setPageTitle]);

  // Update ref when state changes
  useEffect(() => {
    receiptLoadingRef.current = receiptLoading;
  }, [receiptLoading]);

  useEffect(() => {
    // Check for payment success in URL
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('session_id');
    const success = queryParams.get('success');
    const orderId = queryParams.get('order_id');

    if (success && sessionId && orderId) {
      // Update the page title to indicate success
      document.title = "Payment Successful - FredoCloud";
      
      setPaymentSuccess(true);
      setReceiptLoading(true);
      
      // Set a backup timer to hide the loading screen in case of any issues
      const safetyTimer = setTimeout(() => {
        if (receiptLoadingRef.current) {
          console.log('Safety timer triggered - hiding receipt loading UI');
          setReceiptLoading(false);
          // Reset the title when loading finishes
          document.title = "Dashboard - FredoCloud";
        }
      }, 10000); // 10 seconds maximum wait time
      
      // Process successful payment
      processSuccessfulPayment(sessionId, orderId);
      
      // Clean up the safety timer if component unmounts
      return () => {
        clearTimeout(safetyTimer);
        document.title = "Dashboard - FredoCloud";
      };
    } else {
      // Only fetch orders if not processing a payment
      fetchUserOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, user]);

  const processSuccessfulPayment = async (sessionId, orderId) => {
    try {
      if (!user) {
        console.error('Cannot process payment: user not found');
        setReceiptLoading(false);
        document.title = "Dashboard - FredoCloud";
        return;
      }
      
      console.log('Processing successful payment for session:', sessionId);
      const result = await handleCheckoutSuccess(sessionId, orderId, user);
      
      if (result.success) {
        console.log('Payment processed successfully');
        setReceiptProcessed(true);
        // Refresh orders to show updated status
        fetchUserOrders();
      } else {
        console.error('Failed to process payment:', result.error);
      }
    } catch (error) {
      console.error('Error processing successful payment:', error);
    } finally {
      // Hide receipt loading state after processing (with a small delay for UX)
      setTimeout(() => {
        setReceiptLoading(false);
        document.title = "Dashboard - FredoCloud";
      }, 1500);
    }
  };

  const updateOrderStatus = async (orderId, sessionId) => {
    try {
      console.log('Using legacy update order status for session:', sessionId);
      
      // Try to get receipt URL from Stripe
      let receiptUrl = null;
      try {
        receiptUrl = await getReceiptUrl(sessionId);
        console.log('Receipt URL fetched:', receiptUrl ? 'Successfully retrieved' : 'Not available');
      } catch (error) {
        console.error('Error getting receipt URL:', error);
        // Continue even if receipt URL fetch fails
      }
      
      // Get reference to the order document
      const orderRef = doc(db, 'orders', orderId);
      
      // Fetch the order data first for the email
      let orderData = null;
      try {
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          orderData = orderSnap.data();
        }
      } catch (fetchError) {
        console.error('Error fetching order data:', fetchError);
      }
      
      // Update order data
      const updateData = {
        status: 'completed',
        paidAt: new Date().toISOString(),
        sessionId: sessionId // Store session ID for future receipt URL fetching
      };
      
      // Add receipt URL if available
      if (receiptUrl) {
        updateData.receipt_url = receiptUrl;
      }
      
      // Update the order in Firestore
      await updateDoc(orderRef, updateData);
      console.log('Order updated successfully:', orderId);
      
      // Send email notification if receipt URL is available
      if (receiptUrl && user && orderData) {
        try {
          await sendReceiptNotification({
            userEmail: user.email,
            userName: user.name || user.email.split('@')[0],
            orderNumber: orderId,
            packageName: orderData.packageTitle,
            amount: orderData.totalPrice,
            receiptUrl: receiptUrl
          });
          
          console.log('Receipt notification email sent successfully');
        } catch (emailError) {
          console.error('Error sending receipt notification email:', emailError);
          // Continue even if email sending fails
        }
      }
      
      // Refresh orders to show the updated order
      fetchUserOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const fetchUserOrders = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const ordersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setOrders(ordersList);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Count active services (completed orders)
  const activeServices = orders.filter(
    (order) => order.status === "completed"
  ).length;

  // Count pending orders
  const pendingOrders = orders.filter(
    (order) =>
      order.status === "pending" || order.status === "processing_payment"
  ).length;

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        {/* <h1>Dashboard</h1> */}
        <p>Welcome back, {user?.name || "User"}!</p>
      </div>

      {/* Receipt Processing Popup */}
      {receiptLoading && (
        <div className="receipt-loading-overlay">
          <div className="receipt-loading-popup">
            <div className="receipt-loader"></div>
            <h3>Payment Successful!</h3>
            <p>We&apos;re processing your receipt. Please wait a moment...</p>
          </div>
        </div>
      )}

      {paymentSuccess && !receiptLoading && (
        <div className="payment-success">
          <h3>Payment Successful!</h3>
          <p>Your purchase has been completed and your order is now being processed.</p>
          {receiptProcessed && (
            <p>Your receipt has been sent to your email and is also available in your dashboard.</p>
          )}
        </div>
      )}

      <div className="dashboard-stats">
        <TotalAmountCard orders={orders} title="Total Purchases" />
        
        <div className="stat-card">
          <h3>Active Services</h3>
          <div className="stat-value">{activeServices}</div>
        </div>

        <div className="stat-card">
          <h3>Pending Orders</h3>
          <div className="stat-value">{pendingOrders}</div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="row">
          <div className="col-md-8">
            <div className="card">
              <div className="card-header">
                <h2>Your Services</h2>
              </div>

              <div className="card-body">
                {loading ? (
                  <p>Loading your orders...</p>
                ) : orders.length === 0 ? (
                  <p>You haven&apos;t placed any orders yet.</p>
                ) : (
                  <div className="services-list">
                    {orders.map((order) => (
                      <div key={order.id} className="service-item">
                        <div className="service-info">
                          <h4>{order.packageTitle}</h4>
                          <p>
                            Ordered:{" "}
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                          <p>Quantity: {order.quantity}</p>
                          <p>Total: ${order.totalPrice}</p>
                        </div>
                        <div className={`service-status ${order.status}`}>
                          {order.status === "completed"
                            ? "Active"
                            : order.status === "processing_payment"
                            ? "Payment Processing"
                            : "Pending"}
                        </div>
                        {order.status === 'completed' && (
                          <div className="detail-row">
                            <span className="label">Receipt:</span>
                            {order.receipt_url ? (
                              <a 
                                href={order.receipt_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="invoice-link"
                              >
                                View Receipt
                              </a>
                            ) : order.sessionId ? (
                              <ReceiptButton 
                                sessionId={order.sessionId}
                                className="receipt-link-btn"
                                buttonText="View Receipt"
                              />
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card">
              <div className="card-header">
                <h2>Recent Invoices</h2>
              </div>

              <div className="card-body">
                {loading ? (
                  <p>Loading invoices...</p>
                ) : orders.filter((order) => order.status === "completed")
                    .length === 0 ? (
                  <p>No invoices available.</p>
                ) : (
                  <div className="invoices-list">
                    {orders
                      .filter((order) => order.status === "completed")
                      .slice(0, 3)
                      .map((order) => (
                        <div key={order.id} className="invoice-item">
                          <div>
                            <h4>Invoice #{order.id.slice(-6)}</h4>
                            <p>
                              {new Date(
                                order.paidAt || order.createdAt
                              ).toLocaleDateString()}
                            </p>
                            {(order.receipt_url || order.sessionId) && (
                              <div className="invoice-actions">
                                {order.receipt_url ? (
                                  <a 
                                    href={order.receipt_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="receipt-link"
                                  >
                                    View Receipt
                                  </a>
                                ) : order.sessionId && (
                                  <ReceiptButton 
                                    sessionId={order.sessionId} 
                                    className="receipt-link-btn"
                                    buttonText="View Receipt" 
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="invoice-amount">
                            ${order.totalPrice}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="card-footer">
                <a href="/billing" className="btn btn-secondary">
                  View All Invoices
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
